import {applyAnalyticalSheetPresentation, applyImportSheetPresentation} from '../../../../common/upload/excel.utils';
import {UploadPeriodRow} from './upload-history.types';
import * as XLSX from 'xlsx-js-style';
import moment from 'moment';
import {AggregatedDataPoint, ConnectionType, DataType, Role, UserProfile} from '@flowmaps-com/flowmaps-typescriptmodels';
import {Entity} from '../../../../handlers/entity';
import {connectionConsumptionType} from '../connections-overview/connection-consumption-type';
import {measurementIdentity} from '../../../measurements-component/measurement-interval';
import {isManualUploadProvider} from './upload-access';

export const uploadKinds = ['Heat', 'Cooling', 'Electricity', 'Gas', 'Water', 'ChargePoint'];
export const kindLabels: Record<string, string> = {Heat:'Heat', Cooling:'Cooling', Electricity:'Electricity', Gas:'Gas', Water:'Water', ChargePoint:'Charge points'};
export const channelLabels: Record<string,string> = {heatConsumption:'Heat consumption',coolingConsumption:'Cooling consumption',
  electricityConsumption:'Electricity consumption',electricityFeedIn:'Electricity feed-in',gasConsumption:'Gas consumption',
  waterConsumption:'Water consumption',chargePointConsumption:'Charge point consumption'};
export const uploadUnit = (type: string) => /heat|cooling/i.test(type) ? 'GJ' : /gas|water/i.test(type) ? 'm³' : 'kWh';
export const uploadKind = (type: string) => type.startsWith('cooling') ? 'Cooling' : type.startsWith('heat') ? 'Heat'
  : type.startsWith('gas') ? 'Gas' : type.startsWith('water') ? 'Water' : type.startsWith('chargePoint') ? 'ChargePoint' : 'Electricity';

export function canUploadConnection(entity: Entity, profile: UserProfile, now = Date.now()): boolean {
  if (!isManualUploadProvider(entity.connection?.info?.surveyor?.code)) return false;
  return profile?.userRole === Role.admin || !!profile?.receivedAuthorisations?.some(a =>
    !a.revoked && !(a.entityIds?.length || a.aliasIds?.length) && a.nominator === entity.organisation.organisationId
    && [Role.owner, Role.intermediary].includes(a.role)
    && (!a.timeRange?.start || Date.parse(a.timeRange.start) <= now)
    && (!a.timeRange?.end || Date.parse(a.timeRange.end) > now));
}

export function uploadChannels(entity: Entity): DataType[] {
  const main = connectionConsumptionType(entity.connection.info.connectionType);
  return entity.connection.info.connectionType === ConnectionType.Heat ? [main, DataType.coolingConsumption]
    : entity.connection.info.connectionType === ConnectionType.Electricity ? [main, DataType.electricityFeedIn] : [main];
}

export interface WorkbookPeriod {
  connectionId: string; connectionCode: string; dataType: string; month: string; volume: number; count: number;
}
export interface PreparedUpload {
  connectionData: Record<string, Partial<Record<DataType, AggregatedDataPoint[]>>>;
  periods: WorkbookPeriod[];
  pointCount: number;
}

export interface TemplateOptions {year: number; firstMonth: number; lastMonth: number; resolution: 'month'|'hour'|'minute'; channels: string[];}
const fields = ['Location','Connection','Measurement','Unit','Start','End','Value','UTC offset at start (seconds)','Interval duration (seconds)'];
const normalize = (v: unknown) => String(v ?? '').toLowerCase().replace(/³/g,'3').replace(/[^a-z0-9]/g, '');
const empty = (v: unknown) => v == null || v === '';
const MAX_ROWS = 40000;
const dateFormat = 'YYYY-MM-DD[T]HH:mm:ss';
const localFormatters = new Map<string, Intl.DateTimeFormat>();

function localLabel(instant: number, zone: string): string {
  if(!localFormatters.has(zone))localFormatters.set(zone,new Intl.DateTimeFormat('sv-SE', {timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit',
    hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}));
  const parts = localFormatters.get(zone).formatToParts(instant);
  const part=(key:string)=>parts.find(p=>p.type===key)?.value;
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`;
}
function instantOf(local: string, zone: string): number {
  const base=Date.parse(local+'Z'); let instant=base;
  for(let i=0;i<4;i++) {const difference=base-Date.parse(localLabel(instant,zone)+'Z');if(!difference)return instant;instant+=difference;}
  throw new Error('Invalid local time');
}
export function templateIntervals(options: TemplateOptions, zone: string): AggregatedDataPoint[] {
  const {year,firstMonth,lastMonth,resolution}=options;
  if(!Number.isInteger(year)||year<1900||year>2200||firstMonth<1||lastMonth>12||firstMonth>lastMonth) throw new Error('Choose a valid period');
  const start=moment.utc({year,month:firstMonth-1,day:1});
  const end=moment.utc({year,month:lastMonth-1,day:1}).add(1,'month');
  const result: AggregatedDataPoint[]=[];
  if(resolution==='month') {
    for(let at=start.clone();at.isBefore(end);at.add(1,'month')) result.push({timeRange:{start:at.format(dateFormat),end:at.clone().add(1,'month').format(dateFormat)}});
  } else {
    const step=resolution==='minute'?900:3600;
    const limit=instantOf(end.format(dateFormat),zone);
    for(let at=instantOf(start.format(dateFormat),zone);at<limit;at+=step*1000) {
      if(result.length>=MAX_ROWS) throw new Error('Choose a shorter period');
      const local=localLabel(at,zone);
      result.push({timeRange:{start:local,end:localLabel(at+step*1000,zone)},
        utcOffsetSeconds:(Date.parse(local+'Z')-at)/1000,durationSeconds:step});
    }
  }
  return result;
}

function workbook(rows: any[][], translate: (s:string)=>string, description: string, sheetName='Data'): XLSX.WorkBook {
  const book=XLSX.utils.book_new();const sheet=XLSX.utils.aoa_to_sheet(rows);
  for(let row=2;row<=rows.length;row++) sheet['G'+row]={t:'s',v:'',z:'#,##0.########'};
  applyImportSheetPresentation(sheet);
  XLSX.utils.book_append_sheet(book,sheet,sheetName);
  const info=XLSX.utils.aoa_to_sheet([[translate('Instructions')],[description]]);
  applyImportSheetPresentation(info);
  info['!cols']=[{wch:100}];
  info.A2.s.alignment={...info.A2.s.alignment,wrapText:true};
  info['!rows'][1]={hpt:60};
  XLSX.utils.book_append_sheet(book,info,'Info');
  return book;
}

export function createUploadTemplate(entity: Entity, options: TemplateOptions, translate: (s:string)=>string): XLSX.WorkBook {
  const channels=uploadChannels(entity).filter(type=>options.channels.includes(type));
  if(!channels.length)throw new Error('Select at least one source');
  const zone=String(entity.location.info.timeZone || 'Europe/Amsterdam');
  const intervals=templateIntervals(options,zone);
  if(intervals.length*channels.length>MAX_ROWS)throw new Error('Choose a shorter period');
  const rows:any[][]=[fields.map(translate)];
  intervals.forEach(point=>channels.forEach(type=>rows.push([entity.location.info.name,entity.connection.info.code,
    translate(channelLabels[type]),uploadUnit(type),point.timeRange.start,point.timeRange.end,null,point.utcOffsetSeconds,point.durationSeconds])));
  return workbook(rows,translate,translate('Fill only the Value column. Leave missing measurements empty; enter 0 only for measured zero consumption. Keep the connection, unit and time columns unchanged.'));
}

function number(value:unknown):number {
  if(typeof value==='number'&&Number.isFinite(value))return value;
  if(typeof value==='string'&&/^[+-]?\d+(?:[.,]\d+)?$/.test(value.trim()))return Number(value.trim().replace(',','.'));
  throw new Error('A measurement is not a valid number');
}
function date(value:unknown):string {
  if(typeof value==='number') {const d=XLSX.SSF.parse_date_code(value);if(!d)throw new Error('Invalid local time');
    return moment.utc({year:d.y,month:d.m-1,date:d.d,hour:d.H,minute:d.M,second:Math.round(d.S)}).format(dateFormat);}
  const result=moment.utc(String(value),[dateFormat,'YYYY-MM-DD HH:mm:ss','DD-MM-YYYY HH:mm:ss','DD-MM-YYYY HH:mm','YYYY-MM-DD'],true);
  if(!result.isValid())throw new Error('Invalid local time');return result.format(dateFormat);
}

/** Parse first, resolve every destination against the permitted screen scope, then offer a review. */
export function prepareUpload(book:XLSX.WorkBook, connections:Entity[], translate:(s:string)=>string):PreparedUpload {
  const result:PreparedUpload={connectionData:{},periods:[],pointCount:0};const identities=new Map<string,number>();
  const types=[DataType.heatConsumption,DataType.coolingConsumption,DataType.electricityConsumption,DataType.electricityFeedIn,
    DataType.gasConsumption,DataType.waterConsumption,DataType.chargePointConsumption];
  const resolve=(code:unknown)=>{
    const matches=connections.filter(e=>e.connection.info.code===String(code??'').trim());
    if(matches.length!==1)throw new Error(matches.length?'Select one connection when multiple connections have the same code':'The file contains an unknown connection');
    return matches[0];
  };
  const add=(entity:Entity,type:DataType,raw:unknown,start:unknown,end:unknown,offset?:unknown,duration?:unknown,unit?:unknown)=>{
    if(empty(raw))return;
    if(!uploadChannels(entity).includes(type))throw new Error('The measurement does not match the connection');
    if(unit!=null && normalize(unit)!==normalize(uploadUnit(type)))throw new Error('The unit does not match the connection');
    const point:AggregatedDataPoint={value:number(raw),timeRange:{start:date(start),end:date(end)}};
    if(!empty(offset))point.utcOffsetSeconds=number(offset);
    if(!empty(duration))point.durationSeconds=number(duration);
    if(point.durationSeconds!=null&&(!Number.isInteger(point.durationSeconds)||point.durationSeconds<=0))throw new Error('Invalid interval duration');
    if(point.utcOffsetSeconds!=null&&(!Number.isInteger(point.utcOffsetSeconds)||Math.abs(point.utcOffsetSeconds)>64800))throw new Error('Invalid UTC offset');
    if(point.durationSeconds==null&&point.timeRange.start>=point.timeRange.end)throw new Error('Invalid interval duration');
    const identity=entity.connection.connectionId+'|'+type+'|'+measurementIdentity(point);
    if(identities.has(identity)){if(identities.get(identity)!==point.value)throw new Error('The file contains conflicting duplicate measurements');return;}
    identities.set(identity,point.value);
    (result.connectionData[entity.connection.connectionId] ||= {})[type] ||= [];
    result.connectionData[entity.connection.connectionId][type].push(point);result.pointCount++;
    if(result.pointCount>MAX_ROWS)throw new Error('Choose a shorter period');
    const month=point.timeRange.start.slice(0,7);
    let period=result.periods.find(p=>p.connectionId===entity.connection.connectionId&&p.dataType===type&&p.month===month);
    if(!period){period={connectionId:entity.connection.connectionId,connectionCode:entity.connection.info.code,dataType:type,month,volume:0,count:0};result.periods.push(period);}
    period.count++;period.volume+=point.value;
  };
  let recognized=false;
  for(const name of book.SheetNames) {
    if(['info','connections'].includes(name.toLowerCase()))continue;
    const rows=XLSX.utils.sheet_to_json<any[]>(book.Sheets[name],{header:1,defval:null,raw:true});if(rows.length<2)continue;
    const headers=rows[0].map(normalize);
    const index=(...names:string[])=>headers.findIndex(h=>names.map(normalize).includes(h));
    const value=index('Value','Waarde');
    if(value>=0) {
      const code=index('Connection','Aansluiting'),type=index('Measurement','Meting','Meetsoort'),unit=index('Unit','Eenheid');
      const start=index('Start','Van'),end=index('End','Einde','Until','Tot');
      if([code,type,unit,start,end].some(i=>i<0))throw new Error('Use a Flowmaps upload template');
      recognized=true;
      rows.slice(1).forEach(row=>{if(empty(row[value]))return;const entity=resolve(row[code]);
        const dutch:Record<string,string[]>={heatConsumption:['Warmteverbruik'],coolingConsumption:['Koelingverbruik','Koudeverbruik'],
          electricityConsumption:['Elektriciteitsverbruik'],electricityFeedIn:['Teruglevering'],gasConsumption:['Gasverbruik'],waterConsumption:['Waterverbruik'],chargePointConsumption:['Laadpaalverbruik']};
        const dataType=types.find(t=>[t,channelLabels[t],translate(channelLabels[t]),...(dutch[t]||[])].map(normalize).includes(normalize(row[type])));
        if(!dataType)throw new Error('The measurement does not match the connection');
        if(empty(row[unit]))throw new Error('The unit does not match the connection');
        add(entity,dataType,row[value],row[start],row[end],row[index('UTC offset at start (seconds)','UTC-tijdverschil bij start (seconden)')],row[index('Interval duration (seconds)','Intervalduur (seconden)')],row[unit]);});
    } else if(['year','jaar'].includes(headers[0]) && ['ean','aansluiting','connection'].includes(normalize(rows[1]?.[0]))) {
      recognized=true;const year=number(rows[0][1]);
      if(!Number.isInteger(year)||year<1900||year>2200)throw new Error('Choose a valid period');
      rows.slice(2).filter(row=>row.slice(1,13).some(v=>!empty(v))).forEach(row=>{const entity=resolve(row[0]);
        row.slice(1,13).forEach((v,i)=>add(entity,connectionConsumptionType(entity.connection.info.connectionType),v,
          moment.utc({year,month:i,date:1}).format(dateFormat),moment.utc({year,month:i,date:1}).add(1,'month').format(dateFormat)));});
    } else if(index('Start','From','Van')>=0 && index('End','Einde','Until','Tot')>=0) {
      recognized=true;const entity=resolve(name);const start=index('Start','From','Van'),end=index('End','Einde','Until','Tot');
      const consumption=connectionConsumptionType(entity.connection.info.connectionType);
      const columns:[DataType,number][]=[[consumption,index('Consumption','Verbruik')],
        [DataType.electricityFeedIn,index('Feed-in','Feed in','Teruglevering')],
        [DataType.heatConsumption,index('Heat (GJ)','Warmte (GJ)')],[DataType.coolingConsumption,index('Cooling (GJ)','Koude (GJ)')]];
      rows.slice(1).forEach(row=>columns.filter(([,i])=>i>=0).forEach(([type,i])=>add(entity,type,row[i],row[start],row[end],
        row[index('UTC offset at start (seconds)','UTC-tijdverschil bij start (seconden)')],row[index('Interval duration (seconds)','Intervalduur (seconden)')])));
    } else throw new Error('Use a Flowmaps upload template');
  }
  if(!recognized)throw new Error('Use a Flowmaps upload template');
  if(!result.pointCount)throw new Error('The file contains no filled measurement values');
  result.periods.sort((a,b)=>a.month.localeCompare(b.month)||a.connectionCode.localeCompare(b.connectionCode)||a.dataType.localeCompare(b.dataType));
  return result;
}

export function createUploadOverview(rows: UploadPeriodRow[], year: number, translate: (s:string)=>string): XLSX.WorkBook {
  const headings=['Location','Connection','Measurement','Period','Current volume','Unit','Status'];
  const data=rows.map(row=>[row.locationName,row.connectionCode,translate(channelLabels[row.dataType]||row.dataType),row.month,
    row.deleted?null:Number(row.volume),row.unit,translate(row.deleted?'Deleted':row.legacy?'Existing data':'Available')]);
  const sheet=XLSX.utils.aoa_to_sheet([headings.map(translate),...data]);
  for(let row=2;row<=data.length+1;row++) if(sheet['E'+row])sheet['E'+row].z='#,##0.########';
  applyAnalyticalSheetPresentation(sheet,{title:translate('Upload history')+' – '+year});
  const book=XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book,sheet,translate('Period overview'));
  return book;
}
